/**
 * Azure Pub/Sub Notifications Service
 *
 * - Runtime identity MUST be userId string for send_to_user routing.
 * - Uses MultiHub service (same as chat).
 */

import { getMultiHubSocketService } from '../pubsub';

export type NotificationItem = {
  id?: string | number;
  notification_id?: string; // if you still use encrypted id
  type?: string | null;
  title?: string | null;
  content?: string | null;
  link?: string | null;
  is_read?: boolean | null;
  created_at?: string | null;
};

export type NotificationNewCallback = (data: {
  notification: NotificationItem;
  timestamp?: string;
}) => void;

export type UnreadCountCallback = (data: {
  unread_count: number;
  timestamp?: string;
}) => void;

export type SyncResultCallback = (data: {
  notifications: NotificationItem[];
  unread_count: number;
  limit?: number;
  offset?: number;
  unread_only?: boolean;
  timestamp?: string;
}) => void;

export type LicenseRevokedCallback = (data: any) => void;
export type LicenseAssignedCallback = (data: any) => void;
export type ManualQueryUpdateCallback = (data: any) => void;

class PubSubNotificationsService {
  private pubsub = getMultiHubSocketService();
  private hub = 'notifications';
  private listenersInitialized = false;

  private currentUserId: string | null = null;

  private onNotificationNewCallbacks: NotificationNewCallback[] = [];
  private onUnreadCountCallbacks: UnreadCountCallback[] = [];
  private onSyncResultCallbacks: SyncResultCallback[] = [];
  private onLicenseRevokedCallbacks: LicenseRevokedCallback[] = [];
  private onLicenseAssignedCallbacks: LicenseAssignedCallback[] = [];
  private onManualQueryUpdateCallbacks: ManualQueryUpdateCallback[] = [];

  private connectionStatusCallbacks: Array<(connected: boolean) => void> = [];

  constructor(userId?: string) {
    if (userId) {
      this.currentUserId = String(userId);
      this.pubsub.setCurrentUserId(String(userId));
    }
  }

  async initialize(userId: string): Promise<boolean> {
    try {
      this.currentUserId = String(userId);
      this.pubsub.setCurrentUserId(String(userId));

      console.debug(`[NotifService] initialize(userId=${userId}) → connectToHub('${this.hub}')`);
      const connected = await this.pubsub.connectToHub(this.hub);
      if (connected) {
        console.debug(`[NotifService] ✅ connected to '${this.hub}' hub`);
        this.setupEventListeners();
        this.notifyConnectionStatus(true);

        // optional auto-join once connected
        this.joinNotifications();
      } else {
        console.warn(`[NotifService] ❌ connectToHub('${this.hub}') returned false`);
      }
      return connected;
    } catch (error) {
      console.error(`[NotifService] initialize error:`, error);
      return false;
    }
  }

  private setupEventListeners(): void {
    if (this.listenersInitialized) return;
    this.listenersInitialized = true;

    // Catch-all hub listener
    this.pubsub.on(this.hub, '*', (data: any, eventName: string) => {
      console.debug(`[NotifService] event received: "${eventName}"`, data);
      // New notification
      if (eventName === 'notification_new') {
        this.onNotificationNewCallbacks.forEach(cb =>
          cb({
            notification: data?.notification ?? data,
            timestamp: data?.timestamp,
          })
        );
        return;
      }

      // Unread count updates
      if (eventName === 'unread_count_updated') {
        this.onUnreadCountCallbacks.forEach(cb =>
          cb({
            unread_count: Number(data?.unread_count ?? 0),
            timestamp: data?.timestamp,
          })
        );
        return;
      }

      // Sync result
      if (eventName === 'notifications_sync_result') {
        this.onSyncResultCallbacks.forEach(cb =>
          cb({
            notifications: Array.isArray(data?.notifications) ? data.notifications : [],
            unread_count: Number(data?.unread_count ?? 0),
            limit: data?.limit,
            offset: data?.offset,
            unread_only: data?.unread_only,
            timestamp: data?.timestamp,
          })
        );
        return;
      }

      // Optional operational events
      if (eventName === 'license_revoked') {
        this.onLicenseRevokedCallbacks.forEach(cb => cb(data));
        return;
      }

      if (eventName === 'license_assigned') {
        this.onLicenseAssignedCallbacks.forEach(cb => cb(data));
        return;
      }

      if (eventName === 'manual_query_update') {
        this.onManualQueryUpdateCallbacks.forEach(cb => cb(data));
        return;
      }
    });

    this.pubsub.on(this.hub, 'connect_error', (error: any) => {
      this.notifyConnectionStatus(false);
    });

    this.pubsub.on(this.hub, 'disconnect', () => {
      this.notifyConnectionStatus(false);
    });

    this.pubsub.on(this.hub, 'reconnect', () => {
      this.notifyConnectionStatus(true);
      // rejoin after reconnect
      this.joinNotifications();
    });
  }

  joinNotifications(): boolean {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    return this.pubsub.emit(this.hub, 'join_notifications', {
      user_id: this.currentUserId,
    });
  }

  syncNotifications(params?: { limit?: number; offset?: number; unread_only?: boolean }): boolean {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    return this.pubsub.emit(this.hub, 'notifications_sync', {
      user_id: this.currentUserId,
      limit: params?.limit ?? 50,
      offset: params?.offset ?? 0,
      unread_only: params?.unread_only ?? false,
    });
  }

  markNotificationRead(notificationId: string | number, localActionId?: string): boolean {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    return this.pubsub.emit(this.hub, 'notification_mark_read', {
      user_id: this.currentUserId,
      notification_id: String(notificationId),
      local_action_id: localActionId,
    });
  }

  markNotificationReadMultiple(notificationIds: Array<string | number>, localActionId?: string): boolean {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    return this.pubsub.emit(this.hub, 'notification_mark_read_multiple', {
      user_id: this.currentUserId,
      notification_ids: notificationIds.map(String),
      local_action_id: localActionId,
    });
  }

  deleteNotification(notificationId: string | number, localActionId?: string): boolean {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    return this.pubsub.emit(this.hub, 'notification_delete', {
      user_id: this.currentUserId,
      notification_id: String(notificationId),
      local_action_id: localActionId,
    });
  }

  onNotificationNew(cb: NotificationNewCallback): () => void {
    this.onNotificationNewCallbacks.push(cb);
    return () => {
      const i = this.onNotificationNewCallbacks.indexOf(cb);
      if (i > -1) this.onNotificationNewCallbacks.splice(i, 1);
    };
  }

  onUnreadCountUpdated(cb: UnreadCountCallback): () => void {
    this.onUnreadCountCallbacks.push(cb);
    return () => {
      const i = this.onUnreadCountCallbacks.indexOf(cb);
      if (i > -1) this.onUnreadCountCallbacks.splice(i, 1);
    };
  }

  onSyncResult(cb: SyncResultCallback): () => void {
    this.onSyncResultCallbacks.push(cb);
    return () => {
      const i = this.onSyncResultCallbacks.indexOf(cb);
      if (i > -1) this.onSyncResultCallbacks.splice(i, 1);
    };
  }

  onLicenseRevoked(cb: LicenseRevokedCallback): () => void {
    this.onLicenseRevokedCallbacks.push(cb);
    return () => {
      const i = this.onLicenseRevokedCallbacks.indexOf(cb);
      if (i > -1) this.onLicenseRevokedCallbacks.splice(i, 1);
    };
  }

  onLicenseAssigned(cb: LicenseAssignedCallback): () => void {
    this.onLicenseAssignedCallbacks.push(cb);
    return () => {
      const i = this.onLicenseAssignedCallbacks.indexOf(cb);
      if (i > -1) this.onLicenseAssignedCallbacks.splice(i, 1);
    };
  }

  onManualQueryUpdate(cb: ManualQueryUpdateCallback): () => void {
    this.onManualQueryUpdateCallbacks.push(cb);
    return () => {
      const i = this.onManualQueryUpdateCallbacks.indexOf(cb);
      if (i > -1) this.onManualQueryUpdateCallbacks.splice(i, 1);
    };
  }

  onConnectionStatusChange(cb: (connected: boolean) => void): () => void {
    this.connectionStatusCallbacks.push(cb);
    return () => {
      const i = this.connectionStatusCallbacks.indexOf(cb);
      if (i > -1) this.connectionStatusCallbacks.splice(i, 1);
    };
  }

  private notifyConnectionStatus(connected: boolean): void {
    this.connectionStatusCallbacks.forEach(cb => cb(connected));
  }

  isConnected(): boolean {
    return this.pubsub.isConnected(this.hub);
  }

  disconnect(): void {
    this.pubsub.disconnectHub(this.hub);
  }
}

// Singleton
let notifService: PubSubNotificationsService | null = null;

export const getNotificationsService = (userId?: string): PubSubNotificationsService => {
  if (!notifService) notifService = new PubSubNotificationsService(userId);
  return notifService;
};

export default PubSubNotificationsService;
